import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";

export type Language = "ru" | "kz" | "en";

const DEFAULT_LANGUAGE: Language = "ru";
const LANGUAGE_STORAGE_KEY = "baldai.language";
const SUPPORTED_LANGUAGES: Language[] = ["ru", "kz", "en"];

function isLanguage(value: string | null): value is Language {
  if (!value) {
    return false;
  }
  return SUPPORTED_LANGUAGES.includes(value as Language);
}

function getInitialLanguage(): Language {
  if (typeof window === "undefined") {
    return DEFAULT_LANGUAGE;
  }

  const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (isLanguage(storedLanguage)) {
    return storedLanguage;
  }

  return DEFAULT_LANGUAGE;
}

type LanguageContextValue = {
  language: Language;
  setLanguage: (language: Language) => void;
  languages: Language[];
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

type LanguageProviderProps = {
  children: ReactNode;
};

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [language, setLanguage] = useState<Language>(getInitialLanguage);

  useEffect(() => {
    document.documentElement.lang = language;
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  }, [language]);

  return (
    <LanguageContext.Provider
      value={{ language, setLanguage, languages: SUPPORTED_LANGUAGES }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
}

